export interface DomPatchOptions {
  /**
   * Attribute used to match keyed children during reordering.
   * Example: data-key="product-123"
   */
  keyAttribute?: string;
  /**
   * Attribute that controls per-node patch strategy.
   * Values:
   * - full: patch attributes/properties + children (default)
   * - attrs: patch attributes/properties and preserve existing children.
   *   If current element has no meaningful children, children are hydrated once.
   * - none: skip patching this element subtree.
   * Example: data-dom-patch-mode="attrs"
   */
  patchModeAttribute?: string;
  /**
   * Optional custom key resolver. Return null for unkeyed nodes.
   */
  getKey?: (el: Element) => string | null;
  /**
   * Skip morphing the active element itself.
   */
  ignoreActive?: boolean;
  /**
   * Preserve value/selection on the active input or textarea.
   */
  ignoreActiveValue?: boolean;
  /**
   * Attempt to restore focus and selection after patch.
   */
  restoreFocus?: boolean;
  /**
   * Execute script tags after patch.
   * - none: never execute scripts (default)
   * - added: execute only scripts in newly inserted/replaced nodes
   * - all: execute all scripts under the patched root
   */
  executeScripts?: "none" | "added" | "all";
}

const DEFAULT_OPTIONS: Required<
  Pick<
    DomPatchOptions,
    | "keyAttribute"
    | "patchModeAttribute"
    | "ignoreActive"
    | "ignoreActiveValue"
    | "restoreFocus"
    | "executeScripts"
  >
> = {
  keyAttribute: "data-key",
  patchModeAttribute: "data-dom-patch-mode",
  ignoreActive: false,
  ignoreActiveValue: false,
  restoreFocus: false,
  executeScripts: "none",
};

type NodePair = {
  next: Node;
  current: Node;
};

type FocusSnapshot = {
  previousActive: Element | null;
  id: string | null;
  selectionStart: number | null;
  selectionEnd: number | null;
};

type PatchRuntime = {
  activeElement: Element | null;
  ignoreActive: boolean;
  ignoreActiveValue: boolean;
  restoreFocus: boolean;
  executeScripts: DomPatchOptions["executeScripts"];
  focusSnapshot: FocusSnapshot | null;
  scriptRoots: Node[] | null;
};

type DomPatchMode = "full" | "attrs" | "none";

/**
 * Patches `current` in-place to match `next` and returns the live node
 * that now represents `next` in the real DOM.
 */
export function patchDom(next: Node, current: Node, options: DomPatchOptions = {}): Node {
  const keyAttribute = options.keyAttribute ?? DEFAULT_OPTIONS.keyAttribute;
  const patchModeAttribute = options.patchModeAttribute ?? DEFAULT_OPTIONS.patchModeAttribute;
  const getKey = options.getKey ?? ((el: Element) => el.getAttribute(keyAttribute));
  const runtime = createPatchRuntime(options);

  if (next === current) return current;

  if (!canPatchInPlace(next, current)) {
    const replacement = cloneNodeForInsert(next);
    current.parentNode?.replaceChild(replacement, current);
    trackScriptRoot(runtime, replacement);
    finalizePatch(runtime, replacement);
    return replacement;
  }

  const stack: NodePair[] = [{ next, current }];

  while (stack.length) {
    const pair = stack.pop() as NodePair;
    const nextNode = pair.next;
    const currentNode = pair.current;

    if (!canPatchInPlace(nextNode, currentNode)) {
      const replacement = cloneNodeForInsert(nextNode);
      currentNode.parentNode?.replaceChild(replacement, currentNode);
      continue;
    }

    switch (nextNode.nodeType) {
      case Node.TEXT_NODE:
      case Node.COMMENT_NODE: {
        if (currentNode.nodeValue !== nextNode.nodeValue) {
          currentNode.nodeValue = nextNode.nodeValue;
        }
        break;
      }
      case Node.ELEMENT_NODE: {
        const nextEl = nextNode as Element;
        const currentEl = currentNode as Element;
        const patchMode = resolvePatchMode(nextEl, currentEl, patchModeAttribute);

        if (runtime.ignoreActive && currentEl === runtime.activeElement) {
          break;
        }

        if (patchMode === "none") {
          break;
        }

        patchAttributes(nextEl, currentEl, runtime);
        patchElementProperties(nextEl, currentEl, runtime);

        if (patchMode === "attrs") {
          if (!hasMeaningfulChildren(currentEl)) {
            patchChildren(nextEl, currentEl, stack, getKey, runtime);
          }
          break;
        }

        patchChildren(nextEl, currentEl, stack, getKey, runtime);
        break;
      }
      default: {
        patchChildren(nextNode, currentNode, stack, getKey, runtime);
        break;
      }
    }
  }

  finalizePatch(runtime, current);
  return current;
}

function resolvePatchMode(
  nextEl: Element,
  currentEl: Element,
  patchModeAttribute: string
): DomPatchMode {
  const rawMode = (
    nextEl.getAttribute(patchModeAttribute) ??
    currentEl.getAttribute(patchModeAttribute) ??
    ""
  )
    .trim()
    .toLowerCase();

  if (rawMode === "none" || rawMode === "skip") return "none";
  if (rawMode === "attrs" || rawMode === "attributes") return "attrs";
  return "full";
}

function hasMeaningfulChildren(element: Element): boolean {
  const children = element.childNodes;
  for (let i = 0; i < children.length; i++) {
    const node = children[i];
    if (node.nodeType === Node.ELEMENT_NODE) return true;
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return true;
  }
  return false;
}

/**
 * Chooses child reconciliation strategy:
 * - index-based for unkeyed children
 * - keyed diff when any key is present
 */
function patchChildren(
  nextParent: Node,
  currentParent: Node,
  stack: NodePair[],
  getKey: (el: Element) => string | null,
  runtime: PatchRuntime
): void {
  const nextChildren = toNodeArray(nextParent);
  const currentChildren = toNodeArray(currentParent);

  if (!nextChildren.length && !currentChildren.length) return;

  let hasKeys = false;
  for (let i = 0; i < nextChildren.length; i++) {
    if (isKeyed(nextChildren[i], getKey)) {
      hasKeys = true;
      break;
    }
  }
  if (!hasKeys) {
    for (let i = 0; i < currentChildren.length; i++) {
      if (isKeyed(currentChildren[i], getKey)) {
        hasKeys = true;
        break;
      }
    }
  }

  if (!hasKeys) {
    patchChildrenByIndex(nextChildren, currentChildren, currentParent, stack, runtime);
    return;
  }

  patchChildrenKeyed(nextChildren, currentChildren, currentParent, stack, getKey, runtime);
}

/**
 * Index-based child diff for unkeyed lists.
 * Fastest path when children are stable by position.
 */
function patchChildrenByIndex(
  nextChildren: Node[],
  currentChildren: Node[],
  currentParent: Node,
  stack: NodePair[],
  runtime: PatchRuntime
): void {
  const commonLen = Math.min(nextChildren.length, currentChildren.length);

  for (let i = 0; i < commonLen; i++) {
    const nextChild = nextChildren[i];
    const currentChild = currentChildren[i];
    if (canPatchInPlace(nextChild, currentChild)) {
      stack.push({ next: nextChild, current: currentChild });
    } else {
      const replacement = cloneNodeForInsert(nextChild);
      currentParent.replaceChild(replacement, currentChild);
      trackScriptRoot(runtime, replacement);
    }
  }

  for (let i = commonLen; i < nextChildren.length; i++) {
    const appended = cloneNodeForInsert(nextChildren[i]);
    currentParent.appendChild(appended);
    trackScriptRoot(runtime, appended);
  }

  for (let i = currentChildren.length - 1; i >= commonLen; i--) {
    currentParent.removeChild(currentChildren[i]);
  }
}

/**
 * Keyed child diff that preserves/moves existing DOM nodes when keys match.
 * Supports mixed keyed and unkeyed children.
 */
function patchChildrenKeyed(
  nextChildren: Node[],
  currentChildren: Node[],
  currentParent: Node,
  stack: NodePair[],
  getKey: (el: Element) => string | null,
  runtime: PatchRuntime
): void {
  const usedCurrent = new Array<boolean>(currentChildren.length).fill(false);
  const keyedCurrent = new Map<string, number>();

  for (let i = 0; i < currentChildren.length; i++) {
    const key = getNodeKey(currentChildren[i], getKey);
    if (key !== null) keyedCurrent.set(key, i);
  }

  const finalOrder: Node[] = [];
  let unkeyedScan = 0;

  for (let i = 0; i < nextChildren.length; i++) {
    const nextChild = nextChildren[i];
    const key = getNodeKey(nextChild, getKey);

    if (key !== null) {
      const hit = keyedCurrent.get(key);
      if (hit !== undefined && !usedCurrent[hit]) {
        usedCurrent[hit] = true;
        const currentChild = currentChildren[hit];
        if (canPatchInPlace(nextChild, currentChild)) {
          stack.push({ next: nextChild, current: currentChild });
          finalOrder.push(currentChild);
        } else {
          const replacement = cloneNodeForInsert(nextChild);
          currentParent.replaceChild(replacement, currentChild);
          trackScriptRoot(runtime, replacement);
          finalOrder.push(replacement);
        }
      } else {
        const inserted = cloneNodeForInsert(nextChild);
        trackScriptRoot(runtime, inserted);
        finalOrder.push(inserted);
      }
      continue;
    }

    let matchedIndex = -1;
    for (let j = unkeyedScan; j < currentChildren.length; j++) {
      if (usedCurrent[j]) continue;
      const candidate = currentChildren[j];
      if (getNodeKey(candidate, getKey) !== null) continue;
      if (canPatchInPlace(nextChild, candidate)) {
        matchedIndex = j;
        break;
      }
    }

    if (matchedIndex === -1) {
      const inserted = cloneNodeForInsert(nextChild);
      trackScriptRoot(runtime, inserted);
      finalOrder.push(inserted);
    } else {
      usedCurrent[matchedIndex] = true;
      unkeyedScan = matchedIndex + 1;
      const currentChild = currentChildren[matchedIndex];
      stack.push({ next: nextChild, current: currentChild });
      finalOrder.push(currentChild);
    }
  }

  let refNode: Node | null = currentParent.firstChild;
  for (let i = 0; i < finalOrder.length; i++) {
    const node = finalOrder[i];
    if (node === refNode) {
      refNode = refNode?.nextSibling ?? null;
      continue;
    }
    currentParent.insertBefore(node, refNode);
  }

  for (let i = 0; i < currentChildren.length; i++) {
    if (!usedCurrent[i]) {
      const node = currentChildren[i];
      if (node.parentNode === currentParent) currentParent.removeChild(node);
    }
  }
}

/**
 * Sync attributes from `nextEl` to `currentEl` with minimal writes.
 * Removes attributes missing in `nextEl` and updates changed values.
 */
function patchAttributes(nextEl: Element, currentEl: Element, runtime: PatchRuntime): void {
  const currentAttrs = currentEl.attributes;
  for (let i = currentAttrs.length - 1; i >= 0; i--) {
    const name = currentAttrs[i].name;
    if (runtime.ignoreActiveValue && name === "value" && currentEl === runtime.activeElement) {
      continue;
    }
    if (!nextEl.hasAttribute(name)) {
      currentEl.removeAttribute(name);
    }
  }

  const nextAttrs = nextEl.attributes;
  for (let i = 0; i < nextAttrs.length; i++) {
    const attr = nextAttrs[i];
    if (runtime.ignoreActiveValue && attr.name === "value" && currentEl === runtime.activeElement) {
      continue;
    }
    if (currentEl.getAttribute(attr.name) !== attr.value) {
      currentEl.setAttribute(attr.name, attr.value);
    }
  }
}

/**
 * Sync stateful element properties that are not always safe to rely on via attributes.
 * Keeps form controls accurate without replacing the whole node.
 */
function patchElementProperties(nextEl: Element, currentEl: Element, runtime: PatchRuntime): void {
  const preserveActiveValue = runtime.ignoreActiveValue && currentEl === runtime.activeElement;

  if (nextEl instanceof HTMLInputElement && currentEl instanceof HTMLInputElement) {
    if (!preserveActiveValue && currentEl.value !== nextEl.value) currentEl.value = nextEl.value;
    if (currentEl.checked !== nextEl.checked) currentEl.checked = nextEl.checked;
    if (currentEl.disabled !== nextEl.disabled) currentEl.disabled = nextEl.disabled;
    return;
  }

  if (nextEl instanceof HTMLTextAreaElement && currentEl instanceof HTMLTextAreaElement) {
    if (!preserveActiveValue && currentEl.value !== nextEl.value) currentEl.value = nextEl.value;
    if (currentEl.disabled !== nextEl.disabled) currentEl.disabled = nextEl.disabled;
    return;
  }

  if (nextEl instanceof HTMLOptionElement && currentEl instanceof HTMLOptionElement) {
    if (currentEl.selected !== nextEl.selected) currentEl.selected = nextEl.selected;
  }
}

/**
 * True when two nodes can be patched in place instead of replaced.
 * Elements must share the same tag; text/comment only require same node type.
 */
function canPatchInPlace(a: Node, b: Node): boolean {
  if (a.nodeType !== b.nodeType) return false;
  if (a.nodeType !== Node.ELEMENT_NODE) return true;
  return (a as Element).tagName === (b as Element).tagName;
}

/**
 * Converts direct child nodes to a static array for stable iteration during mutations.
 */
function toNodeArray(parent: Node): Node[] {
  const out: Node[] = [];
  let child = parent.firstChild;
  while (child) {
    out.push(child);
    child = child.nextSibling;
  }
  return out;
}

/**
 * Returns true if a node has a non-empty reconciliation key.
 */
function isKeyed(node: Node, getKey: (el: Element) => string | null): boolean {
  return getNodeKey(node, getKey) !== null;
}

/**
 * Resolves the reconciliation key for element nodes.
 */
function getNodeKey(node: Node, getKey: (el: Element) => string | null): string | null {
  if (node.nodeType !== Node.ELEMENT_NODE) return null;
  const key = getKey(node as Element);
  return key === null || key === "" ? null : key;
}

/**
 * Creates a deep clone used for insertion/replacement.
 */
function cloneNodeForInsert(node: Node): Node {
  return node.cloneNode(true);
}

function createPatchRuntime(options: DomPatchOptions): PatchRuntime {
  const ignoreActive = options.ignoreActive ?? DEFAULT_OPTIONS.ignoreActive;
  const ignoreActiveValue = options.ignoreActiveValue ?? DEFAULT_OPTIONS.ignoreActiveValue;
  const restoreFocus = options.restoreFocus ?? DEFAULT_OPTIONS.restoreFocus;
  const executeScripts = options.executeScripts ?? DEFAULT_OPTIONS.executeScripts;

  const needsActiveLookup = ignoreActive || ignoreActiveValue || restoreFocus;
  const activeElement = needsActiveLookup ? (document.activeElement as Element | null) : null;

  return {
    activeElement,
    ignoreActive,
    ignoreActiveValue,
    restoreFocus,
    executeScripts,
    focusSnapshot: restoreFocus ? snapshotFocus(activeElement) : null,
    scriptRoots: executeScripts === "added" ? [] : null,
  };
}

function snapshotFocus(activeElement: Element | null): FocusSnapshot {
  const inputLike =
    activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement;

  return {
    previousActive: activeElement,
    id: activeElement?.getAttribute("id") ?? null,
    selectionStart: inputLike ? activeElement.selectionStart : null,
    selectionEnd: inputLike ? activeElement.selectionEnd : null,
  };
}

function trackScriptRoot(runtime: PatchRuntime, node: Node): void {
  if (runtime.executeScripts !== "added") return;
  runtime.scriptRoots?.push(node);
}

function finalizePatch(runtime: PatchRuntime, patchedRoot: Node): void {
  if (runtime.executeScripts !== "none") {
    executeScripts(runtime, patchedRoot);
  }
  if (runtime.restoreFocus) {
    restoreFocus(runtime, patchedRoot);
  }
}

function executeScripts(runtime: PatchRuntime, patchedRoot: Node): void {
  const roots = runtime.executeScripts === "all" ? [patchedRoot] : runtime.scriptRoots ?? [];
  if (!roots.length) return;

  const scripts: HTMLScriptElement[] = [];
  const seen = new Set<HTMLScriptElement>();

  for (let i = 0; i < roots.length; i++) {
    collectScripts(roots[i], scripts, seen);
  }

  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];
    if (!script.parentNode) continue;
    const replacement = document.createElement("script");

    const attrs = script.attributes;
    for (let j = 0; j < attrs.length; j++) {
      replacement.setAttribute(attrs[j].name, attrs[j].value);
    }
    replacement.text = script.textContent ?? "";
    script.parentNode.replaceChild(replacement, script);
  }
}

function collectScripts(root: Node, out: HTMLScriptElement[], seen: Set<HTMLScriptElement>): void {
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const element = root as Element;

  if (element.tagName === "SCRIPT") {
    const script = element as HTMLScriptElement;
    if (!seen.has(script)) {
      seen.add(script);
      out.push(script);
    }
  }

  const nested = element.querySelectorAll("script");
  for (let i = 0; i < nested.length; i++) {
    const script = nested[i] as HTMLScriptElement;
    if (seen.has(script)) continue;
    seen.add(script);
    out.push(script);
  }
}

function restoreFocus(runtime: PatchRuntime, patchedRoot: Node): void {
  const snapshot = runtime.focusSnapshot;
  if (!snapshot) return;

  let target: Element | null = null;
  if (snapshot.previousActive && snapshot.previousActive.isConnected) {
    target = snapshot.previousActive;
  }

  if (!target && snapshot.id && patchedRoot.nodeType === Node.ELEMENT_NODE) {
    const root = patchedRoot as Element;
    if (root.getAttribute("id") === snapshot.id) {
      target = root;
    } else {
      target = root.querySelector(`[id="${snapshot.id}"]`);
    }
  }

  if (!target || !(target instanceof HTMLElement)) return;
  if (document.activeElement !== target) {
    target.focus({ preventScroll: true });
  }

  if (
    snapshot.selectionStart !== null &&
    snapshot.selectionEnd !== null &&
    (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)
  ) {
    try {
      target.setSelectionRange(snapshot.selectionStart, snapshot.selectionEnd);
    } catch {
      // Ignore unsupported selection ranges.
    }
  }
}

/**
 * Utility for string-based template engines:
 * parse HTML and patch an existing root node.
 */
export function patchDomFromHTML(
  html: string,
  currentRoot: Element,
  options: DomPatchOptions = {}
): Element {
  const tpl = document.createElement("template");
  tpl.innerHTML = html.trim();

  const nextRoot = tpl.content.firstElementChild;
  if (!nextRoot) return currentRoot;

  return patchDom(nextRoot, currentRoot, options) as Element;
}

/**
 * Patches an element's children from an HTML string while keeping the current
 * element as the stable root node.
 */
export function patchDomChildrenFromHTML(
  html: string,
  currentRoot: Element,
  options: DomPatchOptions = {}
): Element {
  const nextRoot = currentRoot.cloneNode(false) as Element;
  nextRoot.innerHTML = html;
  return patchDom(nextRoot, currentRoot, options) as Element;
}

export type DomPatchSchedulerMode = "root" | "children";

export class DomPatchScheduler<T extends Element = Element> {
  private frameId: number | null = null;
  private queuedHtml: string | null = null;
  private readonly root: T;
  private readonly options: DomPatchOptions;
  private readonly mode: DomPatchSchedulerMode;

  constructor(root: T, options: DomPatchOptions = {}, mode: DomPatchSchedulerMode = "root") {
    this.root = root;
    this.options = options;
    this.mode = mode;
  }

  /**
   * Queues HTML for patching and coalesces rapid updates into one patch per animation frame.
   * The latest enqueued HTML wins.
   */
  enqueueHTML(html: string): void {
    this.queuedHtml = html;

    if (this.frameId !== null) return;
    this.frameId = requestAnimationFrame(() => {
      this.frameId = null;
      const payload = this.queuedHtml;
      this.queuedHtml = null;
      if (payload === null) return;
      if (this.mode === "children") {
        patchDomChildrenFromHTML(payload, this.root, this.options);
      } else {
        patchDomFromHTML(payload, this.root, this.options);
      }
    });
  }

  /**
   * Cancels any scheduled frame and drops queued HTML.
   */
  cancel(): void {
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    this.queuedHtml = null;
  }
}
